import { Flex } from "antd";
import CustomDrawer from "../../../components/ui/CustomDrawer";
import WithLoader from "../../../components/WithLoader";
import { useGetInvoiceDetailsQuery } from "../../../features/Invoice/invoice.api";
import { formatDate, formatTime } from "../../../lib/time";
import { CustomModalProps } from "../../../types";
import { addCommaInNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import InvoicesActions from "./_components/InvoiceDetails/InvoicesActions";
import WaitingForApprovalActions from "./_components/InvoiceDetails/WaitingForApprovalActions";
import { INVOICE_STATUS_CONFIG } from "./InvoiceList";

type Props = {
  type?:
    | "invoices"
    | "waiting-for-approval"
    | "waiting-for-payment"
    | "payment";
  data: any;
} & CustomModalProps;

const InvoiceDetails = ({
  type = "waiting-for-approval",
  open,
  handleClose,
  data,
}: Props) => {
  const {
    data: invoiceDetails,
    isLoading,
    isFetching,
  } = useGetInvoiceDetailsQuery(data as number, {
    skip: !data,
  });

  const {
    status,
    job_title,
    po_number,
    invoice_id,
    department,
    businessUnit,
    payment_date,
    created_at,
    vat_amount,
    vat_percentage,
    vat_condition,
    tds_amount,
    tds_percentage,
    tds_condition,
    payment_amount,
    cost_of_company,
    invoice_amount,
    vendor,
    approval_Workflows,
  } = invoiceDetails?.data || {};

  // ------ Function for render generate action ------
  const renderDetailsAction = () => {
    switch (type) {
      case "invoices":
        return (
          <InvoicesActions
            handleClose={handleClose}
            id={data}
            status={status as string}
          />
        );
      case "waiting-for-approval":
        return (
          <WaitingForApprovalActions
            handleClose={handleClose}
            id={data}
            title={`${vendor?.vendor_code} - ${vendor?.business_name}`}
          />
        );
      case "waiting-for-payment":
        return <></>;
      case "payment":
        return <></>;
    }
  };
  return (
    <CustomDrawer
      open={open}
      onClose={() => handleClose && handleClose()}
      title={vendor?.business_name}
      subtitle={`ID: ${vendor?.vendor_code}`}
      image={vendor?.logo || ""}
      size={520}
    >
      <WithLoader isLoading={isLoading || isFetching}>
        <div className="space-y-1.5">
          <Flex justify="space-between">
            <span className="text-sm text-theme-gray-500">Status</span>
            <span
              className={
                INVOICE_STATUS_CONFIG[
                  String(
                    status as string,
                  ).toLowerCase() as keyof typeof INVOICE_STATUS_CONFIG
                ]
              }
            >
              {capitalize((status as string)?.split("_").join(" "))}
            </span>
          </Flex>
          <Flex justify="space-between">
            <span className="text-sm text-theme-gray-500">Job Title</span>
            <span className="text-sm text-theme-gray-900">{job_title}</span>
          </Flex>
          <Flex justify="space-between">
            <span className="text-sm text-theme-gray-500">Payable ID</span>
            <span className="text-sm text-theme-gray-900">{po_number}</span>
          </Flex>
          <Flex justify="space-between">
            <span className="text-sm text-theme-gray-500">Invoice ID</span>
            {/* <span className="text-sm text-theme-primary cursor-pointer underline">
              View
            </span> */}
            <span className="text-sm text-theme-gray-900">{invoice_id}</span>
          </Flex>
          {/* <Flex justify="space-between">
            <span className="text-sm text-theme-gray-500">VAT 6.3</span>
            <span className="text-sm text-theme-primary cursor-pointer underline">
              View
            </span>
          </Flex> */}
          <Flex justify="space-between">
            <span className="text-sm text-theme-gray-500">Business Unit</span>
            <span className="text-sm text-theme-gray-900">
              {businessUnit?.name}
            </span>
          </Flex>
          <Flex justify="space-between">
            <span className="text-sm text-theme-gray-500">Department</span>
            <span className="text-sm text-theme-gray-900">
              {department?.name}
            </span>
          </Flex>
          <Flex justify="space-between">
            <span className="text-sm text-theme-gray-500">Schedule Date</span>
            <span className="text-sm text-theme-gray-900">
              {formatDate(payment_date)}
            </span>
          </Flex>
          <Flex justify="space-between">
            <span className="text-sm text-theme-gray-500">Created At</span>
            <span className="text-sm text-theme-gray-900">
              {formatDate(created_at)} {formatTime(created_at)}
            </span>
          </Flex>
          <div className="p-2.5 rounded-lg bg-theme-primaryRest border border-theme-indigo-100 space-y-1">
            <Flex justify="space-between">
              <span className="text-lg font-semibold text-theme-gray-900">
                Invoice Amount:
              </span>
              <span className="text-lg font-semibold text-theme-primary">
                {addCommaInNumber(invoice_amount || 0)} BDT
              </span>
            </Flex>
            <Flex justify="space-between">
              <span className="text-lg font-normal text-theme-gray-500">
                VAT ({capitalize(vat_condition || "")}):
              </span>
              <span className="text-lg font-normal text-theme-gray-900">
                {addCommaInNumber(vat_amount || 0)} ({vat_percentage}%) BDT
              </span>
            </Flex>
            <Flex justify="space-between">
              <span className="text-lg font-normal text-theme-gray-500">
                AIT ({capitalize(tds_condition || "")}):
              </span>
              <span className="text-lg font-normal text-theme-gray-900">
                {addCommaInNumber(tds_amount || 0)} ({tds_percentage}%) BDT
              </span>
            </Flex>
            <Flex justify="space-between">
              <span className="text-lg font-normal text-theme-gray-500">
                Payable to Vendor:
              </span>
              <span className="text-lg font-semibold text-theme-primary">
                {addCommaInNumber(payment_amount || 0)} BDT
              </span>
            </Flex>
            <Flex justify="space-between">
              <span className="text-lg font-normal text-theme-gray-500">
                Cost to the Company:
              </span>
              <span className="text-lg font-semibold text-theme-error-500">
                {addCommaInNumber(cost_of_company || 0)} BDT
              </span>
            </Flex>
          </div>
        </div>
        <div className="mt-5">{renderDetailsAction()}</div>
        <Flex justify="space-between">
          <span className="text-base font-medium text-theme-gray-800">
            Approval Workflow:
          </span>
        </Flex>
        <div className="mt-2 space-y-1">
          {approval_Workflows?.map((flow) => (
            <div key={flow.id}>
              <Flex justify="space-between" className="-mb-2">
                <span className="text-sm text-theme-gray-500">
                  {flow.business_member?.name}
                </span>
                <span
                  className={
                    INVOICE_STATUS_CONFIG[
                      String(
                        flow.status as string,
                      ).toLowerCase() as keyof typeof INVOICE_STATUS_CONFIG
                    ]
                  }
                >
                  {capitalize((flow.status as string)?.split("_").join(" "))}
                </span>
              </Flex>
              <span className="text-xs text-theme-gray-500 ml-3">
                - {flow.comments || "n/a"}
              </span>
            </div>
          ))}
        </div>
      </WithLoader>
    </CustomDrawer>
  );
};

export default InvoiceDetails;
